import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { NavigationService } from '@core/services/navigation/navigation.service';

import { BaseProductCommand } from '@shared/commands/base-product.command';
import { IProductCommandContext } from '@shared/models/command';
import { ProductTypes } from '@shared/models/products.model';
import { CommandAvailabilityService } from '@shared/services/command-availability.service';

/**
 * Команда для управления уведомлениями конкретной карты
 *
 * Открывает страницу управления PUSH и SMS-уведомлениями для конкретной карты.
 * Команда является точкой входа из карточки продукта в раздел "Действия".
 *
 * Особенности:
 * - Работает только с картами (CARD)
 * - Недоступна для виртуальных карт
 * - Передает ID карты для фильтрации на странице управления
 * - Находится в разделе "Действия" (isInfoAction = false)
 *
 * @example
 * ```typescript
 * const context = { data: { id: 'card-123', isVirtual: false } };
 * await cardNotificationsCommand.execute(context); // /product/card-123/notifications
 * ```
 */
@Injectable({ providedIn: 'root' })
export class CardNotificationsCommand extends BaseProductCommand {
  /** Уникальный идентификатор команды */
  readonly type = 'cardNotifications';

  /** Название команды в меню действий карты */
  readonly title = 'Уведомления';

  /** Иконка для отображения в меню */
  readonly icon = 'notification';

  /** Тип продукта - команда работает только с картами */
  readonly productType = ProductTypes.CARD;

  /** Раздел - "Действия" (false = Действия, true = Прочее) */
  readonly isInfoAction = false;

  /** Порядок отображения в меню действий */
  readonly order = 85;

  /** Не требует проверки серверных настроек */
  readonly settingsNames = ['REQUEST/SMS_NOTIFICATION'];

  constructor(
    commandAvailabilityService: CommandAvailabilityService,
    private readonly navigationService: NavigationService
  ) {
    super(commandAvailabilityService);
  }

  /**
   * Выполнить команду - открыть страницу управления уведомлениями карты
   *
   * Переходит на страницу `/product/:id/notifications` с фильтрацией по ID карты.
   * На странице отображаются PUSH и SMS-уведомления только для этой карты.
   *
   * @param context - контекст карты с ID и данными
   */
  public async execute(context: IProductCommandContext): Promise<void> {
    await this.navigationService.push(`product/${context.data.id}/notifications`, {
      state: { cardId: context.data.id }
    });
  }

  /**
   * Проверить дополнительные условия доступности команды
   *
   * Команда недоступна для виртуальных карт, так как SMS-уведомления
   * могут быть включены только для физических карт.
   *
   * @param context - контекст карты для проверки
   * @returns true если команда доступна, false если недоступна
   */
  protected checkAdditionalAvailability(context: IProductCommandContext): Observable<boolean> | boolean {
    // Недоступна для виртуальных карт
    const isVirtual = context.data.isVirtual || context.data.type === 'VIRTUAL';
    return !isVirtual;
  }
}
